import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import moment from "moment";
import './groupDashboard.css';

const GroupDashboard = ({ data, headerIndexes }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [viewMode, setViewMode] = useState("lastWeek");
  const [revenueComparison, setRevenueComparison] = useState({ lastWeek: 0, previousWeek: 0 });
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [lastMonthParticipants, setLastMonthParticipants] = useState(0);
  const [lastWeekParticipants, setLastWeekParticipants] = useState(0);
  const [participantsByLanguage, setParticipantsByLanguage] = useState({});

  useEffect(() => {
    if (!data || !headerIndexes) return;

    const { "Status": statusIndex, "Start Date": startDateIndex, "Participants (number)": participantsIndex, "Task Name": taskNameIndex, "price": priceIndex, "guide charge": guideChargeIndex } = headerIndexes;

    if ([statusIndex, startDateIndex, participantsIndex, taskNameIndex].some(index => index === undefined)) {
      console.error("🚨 Missing header indexes:", headerIndexes);
      return;
    }

    const today = moment("31/12/2024", "DD/MM/YYYY");
    const lastWeekStart = today.clone().subtract(6, "days");
    const lastMonthStart = today.clone().subtract(1, "month");
    const previousWeekStart = today.clone().subtract(13, "days");
    const previousWeekEnd = today.clone().subtract(7, "days");

    let totalParticipantsCount = 0;
    let lastMonthParticipantsCount = 0;
    let lastWeekParticipantsCount = 0;
    let lastWeekRevenue = 0;
    let previousWeekRevenue = 0;

    const filteredTours = data.filter((row) => row[statusIndex]?.trim() === "NEED PAYMENT" || row[statusIndex]?.trim() === "COMPLETED");
    const groupedData = {};
    const participantsLanguageCount = {};

    filteredTours.forEach(row => {
      const participants = parseInt(row[participantsIndex], 10) || 0;
      const rawDate = row[startDateIndex]?.trim();
      const price = parseFloat(row[priceIndex]) || 0;
      const guideCharge = parseFloat(row[guideChargeIndex]) || 0;

      let momentDate = /^\d{4}\/\d{2}\/\d{2}$/.test(rawDate) ? moment(rawDate, "YYYY/MM/DD", true) : moment(rawDate, "DD/MM/YYYY", true);
      if (!momentDate.isValid()) return;

      totalParticipantsCount += participants;
      if (momentDate.isBetween(lastMonthStart, today, null, "[]")) lastMonthParticipantsCount += participants;
      if (momentDate.isBetween(lastWeekStart, today, null, "[]")) lastWeekParticipantsCount += participants;

      if (momentDate.isBetween(lastWeekStart, today, null, "[]")) {
        lastWeekRevenue += (participants * price) - guideCharge;
        
        const language = row[taskNameIndex]?.split("-")[1]?.trim() || "Unknown";
        if (!participantsLanguageCount[language]) {
          participantsLanguageCount[language] = 0;
        }
        participantsLanguageCount[language] += participants;
      }
      if (momentDate.isBetween(previousWeekStart, previousWeekEnd, null, "[]")) previousWeekRevenue += (participants * price) - guideCharge;

      const groupKey = viewMode === "lastWeek" ? momentDate.format("DD/MM/YYYY") : momentDate.clone().startOf("isoWeek").format("DD/MM/YYYY");
      const language = row[taskNameIndex]?.split("-")[1]?.trim() || "Unknown";

      if (!groupedData[groupKey]) groupedData[groupKey] = {};
      groupedData[groupKey][language] = (groupedData[groupKey][language] || 0) + participants;
    });

    setTotalParticipants(totalParticipantsCount);
    setLastMonthParticipants(lastMonthParticipantsCount);
    setLastWeekParticipants(lastWeekParticipantsCount);
    setRevenueComparison({ lastWeek: lastWeekRevenue, previousWeek: previousWeekRevenue });
    setParticipantsByLanguage(participantsLanguageCount);

    const allLanguages = new Set();
    Object.values(groupedData).forEach(weekData => Object.keys(weekData).forEach(lang => allLanguages.add(lang)));

    const finalChartData = viewMode === "lastWeek"
      ? Array.from({ length: 7 }, (_, i) => {
          const date = lastWeekStart.clone().add(i, "days").format("DD/MM/YYYY");
          const dayData = { date };
          allLanguages.forEach(lang => dayData[lang] = groupedData[date]?.[lang] || 0);
          return dayData;
        })
      : Object.keys(groupedData).sort((a, b) => moment(a, "DD/MM/YYYY").diff(moment(b, "DD/MM/YYYY")))
          .map(date => {
            const weekData = { date };
            allLanguages.forEach(lang => weekData[lang] = groupedData[date]?.[lang] || 0);
            return weekData;
          });

    setFilteredData(finalChartData);
  }, [data, headerIndexes, viewMode]);

  const isProfitPositive = revenueComparison.lastWeek > revenueComparison.previousWeek;
  const profitChangePercentage = ((revenueComparison.lastWeek - revenueComparison.previousWeek) / revenueComparison.previousWeek) * 100 || 0;

  const colorMap = {
    English: "#4CAF50", French: "#2196F3", Spanish: "#F44336", German: "#9C27B0", Italian: "#FF9800", Polish: "#795548", Russian: "#607D8B"
  };
  const getColor = (language) => colorMap[language] || (colorMap[language] = `#${Math.floor(Math.random() * 16777215).toString(16)}`);

  return (
    <div className="groupDashboardWrapper">
      <div className="topContainer">
        <div className="topLeftContainer">
          <div className="revenueContainer">
            <div className="revenueHeading">Revenue</div>
            <div className={`revenueNumber ${isProfitPositive ? 'positive' : 'negative'}`}>
              {profitChangePercentage.toFixed(2)}%
              <span className={`arrow ${isProfitPositive ? 'up' : 'down'}`}>
                <img src={isProfitPositive ? '/trending_up.svg' : '/trending_down.svg'} alt="arrow" className="arrow-icon" />
              </span>
            </div>
            <div className="comparisonContainer">
              <div className="comparisonContainerChild1">
                <div>Current Week</div>
                <span>{revenueComparison.lastWeek.toFixed(2)} $</span>
              </div>
              <div className="comparisonContainerChild2">
                <div>Previous Week</div>
                <span>{revenueComparison.previousWeek.toFixed(2)} $</span>
              </div>
            </div>
          </div>
          <div className="paxConatiner">
            <h3>Participants Summary</h3>
            <p>Total Participants: <span>{totalParticipants}</span></p>
            <p>Participants in Last Month: <span>{lastMonthParticipants}</span></p>
            <p>Participants in Last Week: <span>{lastWeekParticipants}</span></p>
            <div className="participantsByLanguage">
            <h4>Participants by Language (Last Week)</h4>
               <div className="languageOptionContainer">
                  {Object.entries(participantsByLanguage)
                  .sort(([, a], [, b]) => b - a)
                  .map(([language, count]) => (
                    <div key={language} className="languageItem">
                       <span
                         className="languageOptions"
                         style={{
                         backgroundColor: getColor(language),
                       }}
                       ></span>
                      <span className="languageLabel">{`${language}-${count}`}</span>
                     </div>
                   ))}
                  </div>
               </div>
          </div>
        </div>
        <div className="graphContainer">
          <div className="graphDrowpdown">
            <label>
              View Mode:
              <select value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
                <option value="lastWeek">Last 7 Days</option>
                <option value="weekly">Weekly</option>
              </select>
            </label>
          </div>
          {filteredData.length === 0 ? (
            <p>⚠ No data available for selected range.</p>
          ) : (
            <div className="graph-container">
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={filteredData}>
                <XAxis
                 dataKey="date"
                 interval={0}
                 angle={-10}
                 textAnchor="end"
                 tickFormatter={(date) => {
                 if (viewMode === "weekly") {
                 const startDate = moment(date, "DD/MM/YYYY");
                 const endDate = startDate.clone().add(6, "days");
                  return `${startDate.format("DD/MM")} - ${endDate.format("DD/MM")}`;
                  }
                  return date; // For daily view, just show the date
                  }}
                   tick={{ fontSize: '0.6rem', fill: '#333' }}
                  />
                  <YAxis tick={{ fontSize: '0.6rem', fill: '#333' }} />
                  <Tooltip labelStyle={{ fontSize: '10px' }} itemStyle={{ fontSize: '20px' }} contentStyle={{ padding: '6px', fontSize: '12px', backgroundColor: '#f8f9fa', border: '1px solid #ccc', borderRadius: '4px' }} />
                  <Legend wrapperStyle={{ fontSize: '10px', marginTop: "40px" }} layout="horizontal" align="center" verticalAlign="bottom" />
                  {Object.keys(filteredData[0] || {}).filter(key => key !== 'date').map((language, index) => (
                    <Line itemStyle={{ fontSize: '10px', marginTop: "40px" }}  key={index} type="monotone" dataKey={language} stroke={getColor(language)} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupDashboard;
